import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import EmptyTable from "components/EmptyTable";
import { Table, Heading, FieldName, Row, FieldValueWithBorder } from "components/LayoutParts";
import Pagination from "components/Pagination";
import color from "constants/color";
import AimoParkingEdgeDeviceStatusSwitcher from "pages/AimoParking/component/AimoParkingEdgeDeviceStatusSwitcher";
import { AIMO_PARKING_EDGE_DEVICE_STATUS } from "pages/AimoParking/utils/aimoParkingEdgeDeviceStatusUtil";
import { dateFormat } from "utils/dateTimeUtils";

const SELECT_COLUMN_FLEX = 6;

const baseColumns = [
  { label: "シリアルナンバー", flex: 41 },
  { label: "駐車場名", sortKey: "name", flex: 41 },
  { label: "ステータス", flex: 12 },
  { label: "AIバージョン", flex: 12 },
  { label: "登録日", flex: 12 },
];

const CheckboxCell = styled.div<{ flex: number }>`
  display: flex;
  flex: ${(props) => props.flex};
  padding: 0 1%;
  min-height: 41px;
  justify-content: center;
  align-items: center;
  border-right: solid 1px ${color.border};
`;

const HeadingCheckboxCell = styled(CheckboxCell)`
  min-height: auto;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${color.text.link};
`;

type SelectionMode = {
  selectedIds: ReadonlySet<string>;
  onToggle: (device: AimoParkingEdgeDeviceSummary) => void;
  onToggleAllOnPage: (devices: AimoParkingEdgeDeviceSummary[]) => void;
};

const DataTable = ({
  edgeDeviceList,
  totalPages,
  pageIndex,
  setPageIndex,
  selection,
}: {
  edgeDeviceList: AimoParkingEdgeDeviceSummary[];
  totalPages: number;
  pageIndex: number;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  selection?: SelectionMode;
}) => {
  const navigate = useNavigate();

  const onClickRow = (edgeDeviceId: string) => navigate(`/aimo_parking/edge_device/${edgeDeviceId}`);

  const onClickAddEdgeDevice = () => navigate("/aimo_parking/edge_device/create");

  const selectableDevices = edgeDeviceList.filter(
    (d) => d.status !== AIMO_PARKING_EDGE_DEVICE_STATUS.REMOVED
  );
  const allSelectedOnPage =
    selection != null &&
    selectableDevices.length > 0 &&
    selectableDevices.every((d) => selection.selectedIds.has(d.id));

  return (
    <>
      <Table>
        <Heading>
          {selection && (
            <HeadingCheckboxCell flex={SELECT_COLUMN_FLEX}>
              <Checkbox
                checked={allSelectedOnPage}
                onChange={() => selection.onToggleAllOnPage(selectableDevices)}
                aria-label="このページを全選択"
              />
            </HeadingCheckboxCell>
          )}
          {baseColumns.map((column, i) => (
            <FieldName key={column.label} flex={column.flex} lastColumn={i === baseColumns.length - 1}>
              {column.label}
            </FieldName>
          ))}
        </Heading>
        {edgeDeviceList.length > 0 ? (
          edgeDeviceList.map((edgeDevice) => {
            const isRemoved = edgeDevice.status === AIMO_PARKING_EDGE_DEVICE_STATUS.REMOVED;
            const isChecked = selection?.selectedIds.has(edgeDevice.id) ?? false;
            return (
              <Row
                key={edgeDevice.id}
                onClick={() => onClickRow(edgeDevice.id)}
                isDeleted={isRemoved}
              >
                {selection && (
                  <CheckboxCell
                    flex={SELECT_COLUMN_FLEX}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isRemoved) selection.onToggle(edgeDevice);
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      disabled={isRemoved}
                      onChange={() => {
                        if (!isRemoved) selection.onToggle(edgeDevice);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`${edgeDevice.serialNumber} を選択`}
                    />
                  </CheckboxCell>
                )}
                <FieldValueWithBorder flex={baseColumns[0].flex}>
                  {edgeDevice.serialNumber}
                </FieldValueWithBorder>
                <FieldValueWithBorder flex={baseColumns[1].flex}>
                  {edgeDevice.property?.name}
                </FieldValueWithBorder>
                <FieldValueWithBorder flex={baseColumns[2].flex}>
                  <AimoParkingEdgeDeviceStatusSwitcher status={edgeDevice.status} />
                </FieldValueWithBorder>
                <FieldValueWithBorder flex={baseColumns[3].flex}>
                  {edgeDevice.currentAiSoftware?.version ?? "-"}
                </FieldValueWithBorder>
                <FieldValueWithBorder flex={baseColumns[4].flex}>
                  {dateFormat(edgeDevice.createdAt, "YYYY/MM/DD")}
                </FieldValueWithBorder>
              </Row>
            );
          })
        ) : (
          <EmptyTable
            handleClick={onClickAddEdgeDevice}
            label="エッジデバイスを登録"
            message="エッジデバイスが見つかりません"
          />
        )}
      </Table>
      <Pagination totalPages={totalPages} pageIndex={pageIndex} setPageIndex={setPageIndex} />
    </>
  );
};

export default DataTable;
